/**
 * Las direcciones de Compite (rediseño del 14-oct), en un solo lugar.
 *
 * Antes todo vivía en una página con pestañas (`/precio-inteligente?destino=…`).
 * La maqueta parte el módulo en «Día a día» (Decisiones) y «Configuración»
 * (Estrategia), cada una con su dirección. Toda dirección vieja sigue andando:
 * `direccionNuevaDe` la traduce, y el router redirige.
 */

export const RUTAS_COMPITE = {
  decisiones: '/compite/decisiones',
  revisar: '/compite/decisiones/revisar',
  estrategia: '/compite/estrategia',
  monitoreo: '/compite/monitoreo',
} as const;

/** Las pestañas de Estrategia, con su nombre en la URL (`?tab=`, como en compite-arquitectura §2; `?ver=` de antes también se lee). */
export const VISTAS_DE_ESTRATEGIA = ['estrategias', 'reglas', 'productos'] as const;
export type VistaDeEstrategia = (typeof VISTAS_DE_ESTRATEGIA)[number];

export const esVistaDeEstrategia = (v: string | null): v is VistaDeEstrategia =>
  v != null && (VISTAS_DE_ESTRATEGIA as readonly string[]).includes(v);

/** Arma `ruta?a=b&c=d` sin los parámetros vacíos. */
function conParametros(
  ruta: string,
  parametros: Record<string, string | null | undefined>
): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(parametros)) if (v) q.set(k, v);
  const busqueda = q.toString();
  return busqueda ? `${ruta}?${busqueda}` : ruta;
}

/** Las direcciones que usan los enlaces de toda la aplicación. */
export const hrefsDeCompite = {
  decisiones: (o: { vuelta?: string | null } = {}) =>
    conParametros(RUTAS_COMPITE.decisiones, { vuelta: o.vuelta }),
  /** Revisar precios, opcionalmente de una estrategia o de un producto. */
  revisar: (o: { estrategia?: string | null; producto?: string | null } = {}) =>
    conParametros(RUTAS_COMPITE.revisar, { estrategia: o.estrategia, producto: o.producto }),
  estrategias: (o: { vuelta?: string | null } = {}) =>
    conParametros(RUTAS_COMPITE.estrategia, { tab: 'estrategias', vuelta: o.vuelta }),
  reglas: () => conParametros(RUTAS_COMPITE.estrategia, { tab: 'reglas' }),
  /** Monitoreo de cambios, opcionalmente con una revisión abierta (el aviso de la campana). */
  monitoreo: (o: { corrida?: string | null } = {}) =>
    conParametros(RUTAS_COMPITE.monitoreo, { tab: 'cambios', corrida: o.corrida }),
  /** Productos; con `sinCosto` llega filtrado por los que no tienen costo. */
  productos: (o: { sinCosto?: boolean } = {}) =>
    conParametros(RUTAS_COMPITE.estrategia, {
      tab: 'productos',
      filtro: o.sinCosto ? 'sin-costo' : null,
    }),
};

/** Destinos de la página vieja que tienen pantalla nueva. Los demás (con bandera) se quedan. */
const DESTINOS_MUDADOS = ['hoy', 'bandeja', 'productos', 'estrategias', 'reglas'] as const;

/**
 * Traduce una dirección de la página vieja a la nueva. Devuelve null cuando el
 * destino no se mudó (Análisis, Insights y los demás detrás de una bandera
 * interna): esos se siguen viendo en `/precio-inteligente`.
 *
 * Conserva lo que la vieja aceptaba: `?vuelta=`, `?producto=` (Por decidir) y
 * `?ver=sin-costo` (Productos).
 */
export function direccionNuevaDe(params: URLSearchParams): string | null {
  const destino = params.get('destino');
  const vuelta = params.get('vuelta');
  if (destino && !(DESTINOS_MUDADOS as readonly string[]).includes(destino)) return null;
  switch (destino) {
    case 'bandeja':
      return conParametros(RUTAS_COMPITE.revisar, {
        producto: params.get('producto'),
        vuelta,
      });
    case 'productos':
      return conParametros(RUTAS_COMPITE.estrategia, {
        tab: 'productos',
        filtro: params.get('ver') === 'sin-costo' ? 'sin-costo' : null,
        vuelta,
      });
    case 'estrategias':
    case 'reglas':
      return conParametros(RUTAS_COMPITE.estrategia, { tab: destino, vuelta });
    default:
      // Sin destino o «hoy»: la portada del día.
      return conParametros(RUTAS_COMPITE.decisiones, { vuelta });
  }
}
